import { config } from "@/shared/lib/config";
import { addMinutesToTimeHhMm, toTimeHHmmss } from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  formatSlotHours,
  formatSlotHoursFromMinutes,
  resolveBulkSessionSlotDurationMinutes,
  sumBulkSessionSlotDurationMinutes,
  sumMinutesToSlotHours,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  FormInput,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { differenceInMinutes, format, parse } from "date-fns";
import {
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import type {
  BulkScheduleRequestDTO,
  CheckAvailabilityResponseDTO,
} from "../../domain/contracts/bulk-schedule";
import {
  OFF_SITE_DISTANCE_SELECT_OPTIONS,
  type OffSiteDistance,
} from "../../domain/lib/off-site-distance";
import { useCheckBookingAvailability } from "../api/queries";

type SlotDurationMinutes = PrivateLessonSlotDurationMinutes;
const SLOT_DURATION_SET = PRIVATE_LESSON_SLOT_DURATION_SET;
const DURATION_SELECT_OPTIONS = PRIVATE_LESSON_DURATION_SELECT_OPTIONS;

type LookupItem = { id: string; name: string };

const sessionLocationKindFieldZ = z.enum(["studio", "off_site"]);
const offSiteDistanceFieldZ = z.enum(["near", "far"]);
const sessionEngagementKindFieldZ = z.enum([
  "standard",
  "partner_instructor_only",
]);

export const bulkSessionRowSchema = z
  .object({
    date: z.string().min(1, "ຕ້ອງເລືອກວັນທີ"),
    startTime: z.string().min(1, "ຕ້ອງເລືອກເວລາເລີ່ມຕົ້ນ"),
    endTime: z.string().min(1, "ຕ້ອງມີເວລາສິ້ນສຸດ"),
    slotDurationMinutes: z.union([
      z.literal(15),
      z.literal(30),
      z.literal(45),
      z.literal(60),
      z.literal(75),
      z.literal(90),
      z.literal(105),
      z.literal(120),
      z.literal(135),
      z.literal(150),
      z.literal(165),
      z.literal(180),
    ]),
    locationKind: sessionLocationKindFieldZ,
    offSiteDistance: offSiteDistanceFieldZ.optional(),
    offSiteLabel: z.string().optional(),
    engagementKind: sessionEngagementKindFieldZ,
    roomId: z.string().optional(),
    /** ວ່າງ = ໃຊ້ຄູຄ່າເລີ່ມຕົ້ນ */
    instructorIds: z.array(z.string()).optional(),
    notes: z.string().optional(),
  })
  .superRefine((row, ctx) => {
    if (
      row.locationKind === "studio" &&
      (!row.roomId || row.roomId.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກຫ້ອງ",
        path: ["roomId"],
      });
    }
    if (
      row.locationKind === "off_site" &&
      row.roomId &&
      row.roomId.trim().length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງ",
        path: ["roomId"],
      });
    }
    if (row.locationKind === "studio" && row.offSiteDistance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ສະຕູດິໂອບໍ່ໃຊ້ໄລຍະນອກສະຖານທີ່",
        path: ["offSiteDistance"],
      });
    }
    if (row.locationKind === "off_site" && !row.offSiteDistance) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກໄລຍະ (ໃກ້/ໄກ)",
        path: ["offSiteDistance"],
      });
    }
  });

export const BulkScheduleFormSchema = z.object({
  defaultRoomId: z.string().optional(),
  instructorIds: z.array(z.string()).min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ຄູ"),
  sessions: z
    .array(bulkSessionRowSchema)
    .min(1, "ຕ້ອງມີລາຍການຢ່າງໜ້ອຍ 1 ລາຍການ")
    .refine(
      (sessions) => {
        for (const session of sessions) {
          const start = new Date(`2000-01-01T${session.startTime}`);
          const end = new Date(`2000-01-01T${session.endTime}`);
          if (end <= start) return false;
          const mins = differenceInMinutes(end, start);
          if (
            mins !== session.slotDurationMinutes ||
            !SLOT_DURATION_SET.has(mins)
          ) {
            return false;
          }
        }
        return true;
      },
      {
        message: "ເວລາສິ້ນຕ້ອງກົງກັບຄວາມຍາວເລືອກ (15–180 ນາທີ, ທີລະ 15) ແລະຫຼັງເວລາເລີ່ມ",
      },
    ),
});

export type BulkScheduleFormValues = z.infer<typeof BulkScheduleFormSchema>;

type BulkScheduleFormProps = {
  packageId: string;
  remainingHours: number;
  packageLocationKind?: "studio" | "off_site";
  onTotalHoursChange?: (totalHours: number) => void;
  onSubmit: (values: BulkScheduleRequestDTO) => void;
  submitting?: boolean;
};

export function BulkScheduleForm({
  packageId,
  remainingHours,
  packageLocationKind = "studio",
  onTotalHoursChange,
  onSubmit,
  submitting,
}: BulkScheduleFormProps) {
  const isOffSitePackage = packageLocationKind === "off_site";
  const methods = RHF.useForm<BulkScheduleFormValues>({
    resolver: zodResolver(BulkScheduleFormSchema),
    defaultValues: {
      defaultRoomId: "",
      instructorIds: [] as string[],
      sessions: [],
    },
  });

  const sessions = methods.watch("sessions");
  const instructorIds = methods.watch("instructorIds") ?? [];
  const checkAvailability = useCheckBookingAvailability();
  const [availabilityResult, setAvailabilityResult] =
    useState<CheckAvailabilityResponseDTO | null>(null);
  const [hasCheckedAvailability, setHasCheckedAvailability] = useState(false);
  const [internalOverlaps, setInternalOverlaps] = useState<
    Array<{ sessionIndex: number; message: string }>
  >([]);

  const invalidateAvailabilityCheck = useCallback(() => {
    setHasCheckedAvailability(false);
    setAvailabilityResult(null);
    setInternalOverlaps((prev) => (prev.length === 0 ? prev : []));
  }, []);

  useEffect(() => {
    const currentSessions = methods.getValues("sessions") ?? [];
    if (currentSessions.length === 0) return;
    const loc = packageLocationKind;
    methods.setValue(
      "sessions",
      currentSessions.map((session) => ({
        ...session,
        locationKind: loc,
        roomId: loc === "off_site" ? "" : (session.roomId ?? ""),
        offSiteDistance:
          loc === "off_site"
            ? (session.offSiteDistance ?? "near")
            : undefined,
      })),
    );
    if (loc === "off_site") {
      methods.setValue("defaultRoomId", "");
    }
    invalidateAvailabilityCheck();
  }, [packageLocationKind, methods, invalidateAvailabilityCheck]);

  const resolveSlotInstructors = (
    slotInstructors: string[] | undefined,
    defaultIds: string[],
  ): string[] =>
    slotInstructors && slotInstructors.length > 0
      ? slotInstructors
      : defaultIds;

  /** ທັບຊ້ອນໃນບັນຊີດດຽວກັນ: ຫ້ອງດຽວກັນ (ສະຕູດິໂອ) ຫຼື ຄູຮ່ວມກັນ */
  const checkInternalOverlaps = (
    sessionsToCheck: Array<{
      date: string;
      startTime: string;
      endTime: string;
      roomId?: string;
      locationKind?: "studio" | "off_site";
      instructorIds?: string[];
    }>,
    defaultInstructorIds: string[],
  ): Array<{ sessionIndex: number; message: string }> => {
    const overlaps: Array<{ sessionIndex: number; message: string }> = [];

    for (let i = 0; i < sessionsToCheck.length; i++) {
      const session1 = sessionsToCheck[i];
      if (!session1?.date || !session1.startTime || !session1.endTime) {
        continue;
      }

      for (let j = i + 1; j < sessionsToCheck.length; j++) {
        const session2 = sessionsToCheck[j];
        if (!session2?.date || !session2.startTime || !session2.endTime) {
          continue;
        }

        if (session1.date !== session2.date) continue;

        const start1 = new Date(`2000-01-01T${session1.startTime}`);
        const end1 = new Date(`2000-01-01T${session1.endTime}`);
        const start2 = new Date(`2000-01-01T${session2.startTime}`);
        const end2 = new Date(`2000-01-01T${session2.endTime}`);

        if (!(start1 < end2 && end1 > start2)) continue;

        const loc1 = session1.locationKind ?? "studio";
        const loc2 = session2.locationKind ?? "studio";
        const r1 = (session1.roomId ?? "").trim();
        const r2 = (session2.roomId ?? "").trim();
        const sameRoom =
          loc1 === "studio" && loc2 === "studio" && r1.length > 0 && r1 === r2;
        if (sameRoom) {
          const message = "ຫ້ອງທັບຊ້ອນກັບລາຍການອື່ນ";
          if (!overlaps.find((o) => o.sessionIndex === i)) {
            overlaps.push({ sessionIndex: i, message });
          }
          if (!overlaps.find((o) => o.sessionIndex === j)) {
            overlaps.push({ sessionIndex: j, message });
          }
          continue;
        }

        const i1 = resolveSlotInstructors(
          session1.instructorIds,
          defaultInstructorIds,
        );
        const i2 = resolveSlotInstructors(
          session2.instructorIds,
          defaultInstructorIds,
        );
        const shared = i1.filter((id) => i2.includes(id));
        if (shared.length > 0) {
          const message = "ຄູຜູ້ສອນທັບຊ້ອນກັບລາຍການອື່ນ";
          if (!overlaps.find((o) => o.sessionIndex === i)) {
            overlaps.push({ sessionIndex: i, message });
          }
          if (!overlaps.find((o) => o.sessionIndex === j)) {
            overlaps.push({ sessionIndex: j, message });
          }
        }
      }
    }

    return overlaps;
  };

  const totalMinutesToSchedule = useMemo(
    () => sumBulkSessionSlotDurationMinutes(sessions),
    [sessions],
  );

  const totalHoursToSchedule = useMemo(
    () => sumMinutesToSlotHours(totalMinutesToSchedule),
    [totalMinutesToSchedule],
  );

  const hoursBudgetExceeded = totalHoursToSchedule > remainingHours;

  const prevTotalHoursRef = useRef<number | null>(null);
  useEffect(() => {
    if (!onTotalHoursChange) return;
    if (prevTotalHoursRef.current === totalHoursToSchedule) return;
    prevTotalHoursRef.current = totalHoursToSchedule;
    onTotalHoursChange(totalHoursToSchedule);
  }, [totalHoursToSchedule, onTotalHoursChange]);

  const addSession = () => {
    const currentSessions = methods.getValues("sessions");
    const defaultRoom = isOffSitePackage
      ? ""
      : (methods.getValues("defaultRoomId")?.trim() ?? "");
    const loc = packageLocationKind;
    const start = "09:00";
    const slotDurationMinutes: SlotDurationMinutes = 60;
    methods.setValue("sessions", [
      ...currentSessions,
      {
        date: format(new Date(), "yyyy-MM-dd"),
        startTime: start,
        slotDurationMinutes,
        endTime: addMinutesToTimeHhMm(start, slotDurationMinutes),
        locationKind: loc,
        offSiteLabel: "",
        offSiteDistance: loc === "off_site" ? ("near" as const) : undefined,
        engagementKind: "standard" as const,
        roomId: loc === "studio" ? defaultRoom : "",
        instructorIds: [] as string[],
        notes: "",
      },
    ]);
    invalidateAvailabilityCheck();
  };

  const removeSession = (index: number) => {
    const currentSessions = methods.getValues("sessions");
    methods.setValue(
      "sessions",
      currentSessions.filter((_, i) => i !== index),
    );
    invalidateAvailabilityCheck();
  };

  const handleCheckAvailability = async () => {
    if (instructorIds.length === 0 || sessions.length === 0) {
      return;
    }

    const defaultIds = instructorIds as string[];
    const sessionsToCheck = sessions.map((session) => {
      const loc = session.locationKind ?? "studio";
      return {
        date: session.date,
        startTime: session.startTime,
        endTime: session.endTime,
        locationKind: loc,
        engagementKind: session.engagementKind ?? "standard",
        offSiteLabel:
          loc === "off_site" ? session.offSiteLabel?.trim() || null : undefined,
        offSiteDistance:
          loc === "off_site"
            ? (session.offSiteDistance as OffSiteDistance)
            : undefined,
        roomId: loc === "studio" ? session.roomId?.trim() : undefined,
        instructorIds: resolveSlotInstructors(
          session.instructorIds,
          defaultIds,
        ),
      };
    });

    const overlaps = checkInternalOverlaps(sessions, defaultIds);
    setInternalOverlaps(overlaps);

    // If there are internal overlaps, don't check with server
    if (overlaps.length > 0) {
      setHasCheckedAvailability(true);
      setAvailabilityResult({
        available: false,
        conflicts: overlaps.map((overlap) => ({
          sessionIndex: overlap.sessionIndex,
          type: "room" as const,
          message: overlap.message,
        })),
      });
      return;
    }

    // If no internal overlaps, check with server
    try {
      const result = await checkAvailability.mutateAsync({
        sessions: sessionsToCheck,
      });
      setAvailabilityResult(result);
      setHasCheckedAvailability(true);
    } catch {
      // Error handling is done by the mutation
    }
  };

  const handleSubmit = (values: BulkScheduleFormValues) => {
    const sessionsWithDuration = values.sessions.map((session) => {
      const durationMinutes = resolveBulkSessionSlotDurationMinutes(session);
      if (durationMinutes <= 0) {
        throw new Error("Invalid session duration");
      }

      const startTime = toTimeHHmmss(session.startTime);
      const endTime = toTimeHHmmss(
        addMinutesToTimeHhMm(session.startTime, durationMinutes),
      );

      // Parse date string (dd/MM/yyyy) to Date, then format to yyyy-MM-dd
      let formattedDate: string;
      try {
        // Try parsing as dd/MM/yyyy format first
        const parsedDate = parse(session.date, "dd/MM/yyyy", new Date());
        if (Number.isNaN(parsedDate.getTime())) {
          // If parsing fails, try yyyy-MM-dd format (already correct)
          formattedDate = session.date;
        } else {
          formattedDate = format(parsedDate, "yyyy-MM-dd");
        }
      } catch {
        // If all parsing fails, use the date as-is (assuming it's already yyyy-MM-dd)
        formattedDate = session.date;
      }

      const loc = session.locationKind ?? "studio";
      const base = {
        date: formattedDate,
        startTime,
        endTime,
        durationMinutes,
        locationKind: loc,
        engagementKind: session.engagementKind ?? "standard",
        offSiteLabel:
          loc === "off_site" ? session.offSiteLabel?.trim() || null : null,
        offSiteDistance:
          loc === "off_site"
            ? (session.offSiteDistance as OffSiteDistance)
            : null,
        roomId: loc === "studio" ? session.roomId?.trim() : undefined,
        notes: session.notes || undefined,
      };
      if (session.instructorIds && session.instructorIds.length > 0) {
        return { ...base, instructorIds: session.instructorIds };
      }
      return base;
    });

    onSubmit({
      packageId,
      instructorIds: values.instructorIds ?? [],
      sessions: sessionsWithDuration,
    });
  };

  return (
    <FormRoot methods={methods} onSubmit={handleSubmit}>
      <div className="space-y-6">
        {/* Step 1: Default room + instructors */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
              1
            </div>
            <h3 className="font-semibold text-lg">
              {isOffSitePackage
                ? "ຄູຜູ້ສອນຄ່າເລີ່ມຕົ້ນ"
                : "ຫ້ອງ ແລະ ຄູຜູ້ສອນຄ່າເລີ່ມຕົ້ນ"}
            </h3>
            <span className="ml-auto rounded bg-red-100 px-2 py-1 font-medium text-red-700 text-xs">
              ຈຳເປັນ
            </span>
          </div>

          {isOffSitePackage && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border bg-muted/40 p-3">
              <Badge variant="outline">ນອກສະຖານທີ່</Badge>
              <p className="text-muted-foreground text-xs leading-snug">
                ຕາມແມ່ແບບແພັກເກດ — ຈອງເປັນເວລາຄູ ບໍ່ໃຊ້ຫ້ອງ
              </p>
            </div>
          )}

          <div
            className={`grid gap-4 ${isOffSitePackage ? "md:grid-cols-1" : "md:grid-cols-2"}`}
          >
            {!isOffSitePackage && (
              <FormInfiniteCombobox<LookupItem>
                name="defaultRoomId"
                label="ຫ້ອງຄ່າເລີ່ມຕົ້ນ (ລາຍການໃໝ່)"
                queryKey={["rooms", "lookup", "bulk-default"]}
                queryFn={(args) =>
                  fetchLookupForInfinite<LookupItem>(
                    `${config.apiUrl}/master/rooms/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem<LookupItem>(
                    `${config.apiUrl}/master/rooms/lookup`,
                    id,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກຫ້ອງ (ທາງເລືອກ)..."
              />
            )}
            <FormInfiniteMultiCombobox<LookupItem>
              name="instructorIds"
              label="ຄູຜູ້ສອນຄ່າເລີ່ມຕົ້ນ"
              requiredMark
              onFormValueCommit={invalidateAvailabilityCheck}
              queryKey={["instructors", "lookup"]}
              queryFn={(args) =>
                fetchLookupForInfinite<LookupItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  args,
                )
              }
              preloadQueryFn={(ids) =>
                hydrateLookupItems<LookupItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  ids,
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກຄູຜູ້ສອນ..."
            />
          </div>
        </div>

        {/* Step 2: Schedule Batches */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
              2
            </div>
            <h3 className="font-semibold text-lg">ກຳນົດຕາຕະລາງຮຽນ</h3>
            <span className="ml-auto rounded bg-gray-100 px-2 py-1 font-medium text-gray-700 text-xs dark:bg-gray-800 dark:text-gray-300">
              Batch Entry Mode
            </span>
          </div>

          <div className="space-y-4">
            {/* Sessions Table */}
            {sessions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="p-2 text-left font-semibold text-sm">
                        ວັນທີ
                      </th>
                      <th className="p-2 text-left font-semibold text-sm">
                        ເວລາເລີ່ມຕົ້ນ
                      </th>
                      <th className="whitespace-nowrap p-2 text-left font-semibold text-sm">
                        ຄວາມຍາວ (ນາທີ)
                      </th>
                      <th className="p-2 text-left font-semibold text-sm">
                        ເວລາສິ້ນສຸດ
                      </th>
                      {!isOffSitePackage ? (
                        <th className="min-w-[160px] p-2 text-left font-semibold text-sm">
                          ຫ້ອງ
                        </th>
                      ) : (
                        <>
                          <th className="min-w-[120px] p-2 text-left font-semibold text-sm">
                            ໄລຍະ
                          </th>
                          <th className="min-w-[160px] p-2 text-left font-semibold text-sm">
                            ຊື່ສະຖານທີ່
                          </th>
                        </>
                      )}
                      <th className="min-w-[200px] p-2 text-left font-semibold text-sm">
                        ຄູ (ສະເພາະລາຍການ)
                      </th>
                      <th className="p-2 text-left font-semibold text-sm">
                        ລາຍການ
                      </th>
                      <th className="p-2 text-left font-semibold text-sm">
                        ການກະທຳ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session, index) => {
                      let hours = "0";
                      const minutes = resolveBulkSessionSlotDurationMinutes(session);
                      if (minutes > 0) {
                        hours = formatSlotHoursFromMinutes(minutes);
                      }

                      // Check if this session has conflicts (from server or internal overlaps)
                      const sessionConflicts =
                        availabilityResult?.conflicts.filter(
                          (c) => c.sessionIndex === index,
                        ) || [];
                      const internalOverlap = internalOverlaps.find(
                        (o) => o.sessionIndex === index,
                      );
                      const hasConflict =
                        sessionConflicts.length > 0 || !!internalOverlap;
                      const isAvailable =
                        hasCheckedAvailability && !hasConflict;

                      // Get conflict message (prioritize internal overlap message)
                      const conflictMessage =
                        internalOverlap?.message ||
                        sessionConflicts[0]?.message ||
                        "";

                      return (
                        <tr
                          key={`slot-${String(index)}`}
                          className={`border-b ${
                            hasCheckedAvailability
                              ? hasConflict
                                ? "bg-red-50 dark:bg-red-950/20"
                                : "bg-green-50 dark:bg-green-950/20"
                              : ""
                          }`.trim()}
                        >
                          <td className="p-2">
                            <RHF.Controller
                              name={`sessions.${index}.date`}
                              control={methods.control}
                              render={({ field }) => (
                                <input
                                  type="date"
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    invalidateAvailabilityCheck();
                                  }}
                                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                />
                              )}
                            />
                          </td>
                          <td className="p-2">
                            <RHF.Controller
                              name={`sessions.${index}.startTime`}
                              control={methods.control}
                              render={({ field }) => (
                                <QuarterHourMinuteSelect
                                  value={field.value ?? ""}
                                  onChange={(v) => {
                                    field.onChange(v);
                                    const dur = methods.getValues(
                                      `sessions.${index}.slotDurationMinutes`,
                                    ) as SlotDurationMinutes;
                                    methods.setValue(
                                      `sessions.${index}.endTime`,
                                      addMinutesToTimeHhMm(v, dur),
                                    );
                                    invalidateAvailabilityCheck();
                                  }}
                                />
                              )}
                            />
                          </td>
                          <td className="p-2">
                            <RHF.Controller
                              name={`sessions.${index}.slotDurationMinutes`}
                              control={methods.control}
                              render={({ field: durField }) => {
                                const raw = Number(durField.value);
                                const safe = SLOT_DURATION_SET.has(raw)
                                  ? raw
                                  : 60;
                                return (
                                  <SimpleSelect
                                    value={String(safe)}
                                    onValueChange={(val) => {
                                      const m = Number(val);
                                      if (!SLOT_DURATION_SET.has(m)) return;
                                      durField.onChange(m);
                                      const st = methods.getValues(
                                        `sessions.${index}.startTime`,
                                      );
                                      if (st) {
                                        methods.setValue(
                                          `sessions.${index}.endTime`,
                                          addMinutesToTimeHhMm(st, m),
                                        );
                                      }
                                      invalidateAvailabilityCheck();
                                    }}
                                    options={DURATION_SELECT_OPTIONS}
                                    className="h-9 w-full"
                                    placeholder="ເລືອກຄວາມຍາວ"
                                  />
                                );
                              }}
                            />
                          </td>
                          <td className="p-2">
                            <RHF.Controller
                              name={`sessions.${index}.endTime`}
                              control={methods.control}
                              render={({ field }) => (
                                <div className="flex h-9 w-full items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                                  {field.value || "—"}
                                </div>
                              )}
                            />
                          </td>
                          {!isOffSitePackage ? (
                            <td className="p-2">
                              <FormInfiniteCombobox<LookupItem>
                                key={`room-${String(index)}`}
                                name={`sessions.${index}.roomId`}
                                onFormValueCommit={invalidateAvailabilityCheck}
                                queryKey={["rooms", "lookup", String(index)]}
                                queryFn={(args) =>
                                  fetchLookupForInfinite<LookupItem>(
                                    `${config.apiUrl}/master/rooms/lookup`,
                                    args,
                                  )
                                }
                                preloadQueryFn={(id) =>
                                  hydrateLookupItem<LookupItem>(
                                    `${config.apiUrl}/master/rooms/lookup`,
                                    id,
                                  )
                                }
                                getLabel={(item) => item.name}
                                getValue={(item) => item.id}
                                placeholder="ເລືອກຫ້ອງ..."
                              />
                            </td>
                          ) : (
                            <>
                              <td className="p-2">
                                <RHF.Controller
                                  name={`sessions.${index}.offSiteDistance`}
                                  control={methods.control}
                                  render={({ field }) => (
                                    <SimpleSelect
                                      value={field.value ?? "near"}
                                      onValueChange={(v) => {
                                        field.onChange(v as OffSiteDistance);
                                        invalidateAvailabilityCheck();
                                      }}
                                      options={[
                                        ...OFF_SITE_DISTANCE_SELECT_OPTIONS,
                                      ]}
                                      className="h-9 w-full"
                                    />
                                  )}
                                />
                              </td>
                              <td className="p-2">
                                <FormInput
                                  name={`sessions.${index}.offSiteLabel`}
                                  placeholder="ຕົວຢ່າງ: ໂຮງຮຽນ ມປລ"
                                />
                              </td>
                            </>
                          )}
                          <td className="p-2 align-top">
                            <FormInfiniteMultiCombobox<LookupItem>
                              name={`sessions.${index}.instructorIds`}
                              onFormValueCommit={invalidateAvailabilityCheck}
                              queryKey={[
                                "instructors",
                                "lookup",
                                "slot",
                                String(index),
                              ]}
                              queryFn={(args) =>
                                fetchLookupForInfinite<LookupItem>(
                                  `${config.apiUrl}/master/instructors/lookup`,
                                  args,
                                )
                              }
                              preloadQueryFn={(ids) =>
                                hydrateLookupItems<LookupItem>(
                                  `${config.apiUrl}/master/instructors/lookup`,
                                  ids,
                                )
                              }
                              getLabel={(item) => item.name}
                              getValue={(item) => item.id}
                              placeholder="ໃຊ້ຄ່າເລີ່ມຕົ້ນ"
                            />
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{hours} ຊົ່ວໂມງ</span>
                              {hasCheckedAvailability && (
                                <div className="flex items-center gap-1">
                                  {isAvailable ? (
                                    <CheckCircleIcon className="size-4 text-green-600" />
                                  ) : (
                                    <XCircleIcon className="size-4 text-red-600" />
                                  )}
                                  {hasConflict && (
                                    <span className="text-red-600 text-xs">
                                      {conflictMessage}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSession(index)}
                            >
                              <TrashIcon className="size-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={addSession}
              disabled={
                instructorIds.length === 0 ||
                totalHoursToSchedule >= remainingHours
              }
            >
              <PlusIcon className="mr-2 size-4" />
              ເພີ່ມລາຍການອີກ
            </Button>

            {instructorIds.length === 0 && (
              <Alert>
                <AlertDescription>ກະລຸນາເລືອກຄູຜູ້ສອນກ່ອນເພີ່ມລາຍການ</AlertDescription>
              </Alert>
            )}
            {instructorIds.length > 0 &&
              totalHoursToSchedule >= remainingHours &&
              sessions.length > 0 && (
                <Alert>
                  <AlertDescription>
                    ຊົ່ວໂມງຕາມຕາຕະລາງຄົບຕາມຍອດເຫຼືອແລ້ວ — ລົບ ຫຼື
                    ປັບເວລາກ່ອນເພີ່ມລາຍການ
                  </AlertDescription>
                </Alert>
              )}
            {hoursBudgetExceeded && (
              <Alert variant="destructive">
                <AlertDescription>
                  ຍອດຊົ່ວໂມງທີ່ຈະຫັກ ({formatSlotHours(totalHoursToSchedule)} ຊົ່ວໂມງ) ເກີນ
                  {remainingHours} ຊົ່ວໂມງທີ່ເຫຼືອໃນແພັກເກດ
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        {/* Summary */}
        {sessions.length > 0 && (
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">ລາຍການທັງໝົດ:</span>
                <p className="font-bold">{sessions.length} ຄັ້ງ</p>
              </div>
              <div>
                <span className="text-muted-foreground">
                  ຊົ່ວໂມງທີ່ຈະຫັກ:
                </span>
                <p className="font-bold">
                  {formatSlotHours(totalHoursToSchedule)} ຊົ່ວໂມງ — ເຫຼືອໃນແພັກເກດ{" "}
                  {formatSlotHours(remainingHours)} ຊົ່ວໂມງ
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Important Information */}
        <div className="space-y-2 rounded-lg border bg-blue-50 p-4 dark:bg-blue-950/20">
          <p className="font-medium text-sm">ຂໍ້ມູນສຳຄັນ:</p>
          <ul className="space-y-1 text-blue-900 text-sm dark:text-blue-200">
            <li>• ລະບົບຈະກວດສອບຄວາມຂັດແຍ້ງຂອງຫ້ອງແລະຄູຜູ້ສອນກ່ອນຢືນຢັນສິ້ນສຸດ</li>
            <li>• ຊົ່ວໂມງຈະຖືກຫັກຈາກຍອດແພັກເກດພຽງເມື່ອກົດ "ຢືນຢັນ"</li>
            <li>• ລະບົບຈະສົ່ງອີເມວຢືນຢັນຕາຕະລາງຮຽນໃຫ້ນັກຮຽນອັດຕະໂນມັດ</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" disabled={submitting}>
            ຍົກເລີກການປ່ຽນແປງ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleCheckAvailability}
            disabled={
              submitting ||
              sessions.length === 0 ||
              instructorIds.length === 0 ||
              hoursBudgetExceeded ||
              checkAvailability.isPending
            }
          >
            {checkAvailability.isPending ? "ກຳລັງກວດສອບ..." : "ກວດສອບຄວາມພ້ອມ"}
          </Button>
          <Button
            type="submit"
            className="bg-red-600 hover:bg-red-700"
            disabled={
              submitting ||
              sessions.length === 0 ||
              instructorIds.length === 0 ||
              hoursBudgetExceeded ||
              !hasCheckedAvailability ||
              !availabilityResult?.available
            }
          >
            {submitting ? "ກຳລັງຈອງ..." : "ຢືນຢັນແລະຈອງທັງໝົດ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
